import { atomWithReset } from 'jotai/utils';

import type { CustomIdField, LoginCode, LoginRequest, LoginStatus, User } from '@/types/auth';

/** The custom id field to use during login */
export const customIdFieldAtom = atomWithReset<CustomIdField>(
  {
    description: '',
    issuer: '',
    placeholder: '',
    regex: '',
  }
);

/** Whether the user info is currently saving to the API. */
export const isSavingUserAtom = atomWithReset<boolean>(false);

/** The login code for use in future login. */
export const loginCodeAtom = atomWithReset<LoginCode>(
  {
    id: '',
    batch: '',
    createdAt: '',
    customIdField: {
      description: '',
      issuer: '',
      placeholder: '',
      regex: '',
    },
    expiresAt: '',
    isEmailed: false,
    languages: ['en-US'],
    project: '',
    respondent: '',
    value: ''
  }
);

/** Error message thrown when trying to log in. */
export const loginErrorAtom = atomWithReset<Error | null>(null);

/** Info a respondent submits to log in */
export const loginRequestAtom = atomWithReset<LoginRequest>(
  {
    backupOneTimePassword: null,
    customIdValue: null,
    loginCodeValue: '',
    method: 'C',
    password: null,
    oneTimePassword: null,
  }
);

/**
 * A LoginStatus object recording (1) the login status code (a 2-character string) for the most recent attempt,
 * (2) a list of the unique previous status codes, (3) an error message to show on screen.
 */
export const loginStatusAtom = atomWithReset<LoginStatus>(
  {
    currentStatusCode: 'ML',
    errorMessage: '',
    previousStatusCodes: []
  }
);

/** A user object describing someone who can log in to gain additional permissions */
export const userAtom = atomWithReset<User>(
  {
    id: '',
    accountUserRelations: [],
    customId: '',
    createdAt: '',
    emailAddress: '',
    isEmailAddressVerificationRequired: false,
    isEmailAddressVerified: false,
    issuer: '',
    firstName: '',
    isActive: true,
    isStaff: false,
    lastName: '',
    respondents: [],
    url: ''
  }
);

/** Error message returned when trying to save the user information to the API. */
export const userErrorMessageAtom = atomWithReset<string | null>(null);